import getUnixTime from 'date-fns/getUnixTime'
import React, { useState, useEffect, useMemo } from 'react'

//
import { ButtonNew, HeroDual, Loader, ToggleGroup, Wistia } from '../../../storybook'
import Hero from '../../../storybook/Hero/Hero'
import { getWistiaAspectRatio } from '../../../utils/common'
import { getMicroCopy } from '../../../utils/microcopy'
import { LocationContext, SiteDataContext } from '../../page-wrapper'

import TestimonialsListing from './testimonials'
import { useMicroCopyData } from '@/src/context/MicroCopyContext'
import { BUTTON_VARIANT } from '../../../storybook/ButtonNew/ButtonNew'
import { Image as DatoImage } from 'react-datocms'
import { useRouter } from 'next/router'

const query = `
  query ($locale: SiteLocale!, $marketId: [ItemId]!) {
    allCustomerModels(
      locale: $locale,
      filter: { enabledMarkets: { anyIn: $marketId }, enabledLocales: { eq: true }}
      first: 200
    ) {
      id
      _firstPublishedAt
      customerType
      highlight
      title(fallbackLocales: en) 
      quote(fallbackLocales: en) 
      name
      slug
      type: typeField {
        key
        value
      }
      logo {
        title
        url
        width
        height
      }
      image(fallbackLocales: [en]) {
        url
        height
        width
        responsiveImage(imgixParams: {fit: clip, h: 400, auto: format}) {
          srcSet
          sizes
          src
          width
          height
          aspectRatio
          alt
          title
          bgColor
          base64
        }
      }
    }
  }
`

const BlockCustomersTop = ({
  data,
  data: { title, description, defaultStoryHighlight, customerTypesFilter, customerTypes = [] }
}) => {
  const router = useRouter()
  const [selectedType, setSelectedType] = useState('')
  const [isLoading, setIsLoading] = React.useState(true)
  const [customers, setCustomers] = React.useState(null)

  const microCopyData = useMicroCopyData()
  const location = React.useContext(LocationContext)
  const { lang, market } = React.useContext(SiteDataContext)

  useEffect(() => {
    setSelectedType('')
  }, [location.query.locale])

  useEffect(() => {
    const variables = {
      locale: data.extra.locale,
      marketId: [market.id]
    }

    window.scrollTo({ top: 0 })

    const fetchData = async () => {
      try {
        const response = await fetch('/api/dato-fetch', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query, variables })
        })
        const res = await response.json()
        if (response.ok) {
          const customerStoriesFetched =
            res?.allCustomerModels.map((customer) => {
              if (customer?._firstPublishedAt) {
                const timestamp = getUnixTime(new Date(customer?._firstPublishedAt))
                return { ...customer, customerType: customer.type, timestamp }
              }
              return customer
            }) || []

          customerStoriesFetched
            .sort((a, b) => {
              if (a?.highlight) return -1
              if (a?.timestamp > b?.timestamp) {
                return -1
              }
              if (a?.timestamp === b?.timestamp) {
                return 0
              }
              if (a?.timestamp < b?.timestamp) {
                return 1
              }
              return -1
            })
            .sort((a) => (a.highlight ? -1 : 1))

          setCustomers(customerStoriesFetched)

          window.scrollTo({ top: 0 })
          setIsLoading(false)
        } else {
          console.error('Failed to fetch data:', res.error)
          // Handle error appropriately
        }
      } catch (error) {
        console.error('Error fetching data:', error)
        // Handle error appropriately
      }
    }

    fetchData()

    const hash = location.asPath.split('#')[1] || null
    if (!hash) return
    const decodedHash = decodeURIComponent(hash)
    if (!customerTypesFilter.includes(decodedHash)) return

    setSelectedType(decodedHash)
  }, [router.asPath])

  function setAnchor(value) {
    window.location.hash = value
  }

  const handleToggle = (value) => {
    setAnchor(value)
    setSelectedType(value)
  }

  const filterArray = useMemo(() => {
    return [
      {
        id: 'item1',
        value: '',
        label: getMicroCopy({ key: `allStories`, data: microCopyData[lang] })
      },
      ...customerTypes.map((type, i) => {
        return {
          id: 'item' + (i + 2),
          value: type.key,
          label: getMicroCopy({ key: type.key, data: microCopyData[lang] })
        }
      })
    ]
  }, [customerTypesFilter, lang, microCopyData])

  const selectedStoryHighlight =
    customers && typeof selectedType === 'string' && selectedType.length > 0
      ? customers.find((customer) => customer?.type?.key === selectedType)
      : defaultStoryHighlight

  const customersToShow = selectedType
    ? customers?.filter(
        (customer) =>
          customer?.type.key === selectedType && customer?.slug !== selectedStoryHighlight?.slug
      )
    : customers?.filter((customer) => customer?.slug !== selectedStoryHighlight?.slug)

  return (
    <>
      <Hero
        key={'customer-toggle-group'}
        title={title}
        content={description}
        background="c-hero--green-pattern l-section"
      >
        <ToggleGroup activeValue={selectedType} handleToggle={handleToggle} items={filterArray} />
      </Hero>
      {!isLoading ? (
        selectedStoryHighlight || customersToShow?.length > 0 ? (
          <div className="u-margin-top:-16 u-border-radius-top u-background-color:white">
            <HeroDual
              key="story-highlight"
              headingType="h3"
              classes="c-customer-top__title-uppercase"
              logo={selectedStoryHighlight?.logo}
              title={selectedStoryHighlight?.title}
              text={selectedStoryHighlight?.description || selectedStoryHighlight?.quote}
              eyebrow={
                selectedStoryHighlight?.customerType
                  ? getMicroCopy({
                      key: `${selectedStoryHighlight?.type?.key}`,
                      data: microCopyData[lang]
                    })
                  : ''
              }
              prefaceBelow={false}
              media={
                selectedStoryHighlight.video ? (
                  <Wistia
                    key={selectedStoryHighlight.video.videoId}
                    videoId={selectedStoryHighlight.video.videoId}
                    videoSource={selectedStoryHighlight.video.videoSource}
                    thumbnail={
                      selectedStoryHighlight?.video?.thumbnail ||
                      selectedStoryHighlight.image ||
                      null
                    }
                    aspectRatioX={
                      getWistiaAspectRatio(selectedStoryHighlight?.video?.aspectRatio).x
                    }
                    aspectRatioY={
                      getWistiaAspectRatio(selectedStoryHighlight?.video?.aspectRatio).y
                    }
                    cover={selectedStoryHighlight?.video?.cover}
                    autoPlay={selectedStoryHighlight?.video?.autoPlay}
                    controlsVisibleOnLoad={selectedStoryHighlight?.video?.controlsVisibleOnLoad}
                    muted={selectedStoryHighlight?.video?.muted}
                    transparentBackground={selectedStoryHighlight?.video?.transparentBackground}
                    popover={selectedStoryHighlight?.video?.popover}
                    hideControls={selectedStoryHighlight?.video?.hideControls}
                    playText={selectedStoryHighlight?.wistiaVideo?.playText}
                  />
                ) : selectedStoryHighlight?.image?.responsiveImage ? (
                  <DatoImage
                    data={selectedStoryHighlight?.image?.responsiveImage}
                    className={'c-blog-top__image-highlight'}
                    style={{ objectFit: 'contain' }}
                    loading={'eager'}
                  />
                ) : null
              }
            >
              <ButtonNew
                variant={BUTTON_VARIANT.GREEN}
                url={`/${data.extra.slugPrefix}/customers/${selectedStoryHighlight.slug}`}
              >
                {getMicroCopy({ key: `readTestimonial`, data: microCopyData[lang] })}
              </ButtonNew>
            </HeroDual>

            <TestimonialsListing
              customers={customersToShow}
              slugPrefix={`${data.extra.slugPrefix}/`}
              limit={12}
            />
          </div>
        ) : null
      ) : (
        <section className="l-section u-margin-top:-16 u-border-radius-top u-background-color:white">
          <div className="l-container:12/12 l-container--default-spacing">
            <Loader />
          </div>
        </section>
      )}
    </>
  )
}

export default BlockCustomersTop
